/**
 * Utility functions for transforming masterdata values to primitive types
 */

/**
 * Extracts the ID from masterdata object and converts to number
 * @param masterData - Masterdata object with id property, or number, or undefined
 * @returns number | undefined
 */
export function masterDataToNumber(
	masterData: { id: string | number } | number | null | undefined,
): number | undefined {
	// If it's already a number, return it
	if (typeof masterData === 'number') {
		return masterData;
	}

	// If it's null or undefined, return undefined
	if (!masterData || typeof masterData !== 'object') {
		return undefined;
	}

	// If it's a masterdata object, extract the id
	if ('id' in masterData && masterData.id != null) {
		const id =
			typeof masterData.id === 'string'
				? parseInt(masterData.id, 10)
				: masterData.id;
		// Ensure it's a valid number
		return isNaN(id) ? undefined : id;
	}

	return undefined;
}

/**
 * Extracts the ID from masterdata object and converts to number with fallback
 * @param masterData - Masterdata object with id property
 * @param fallback - Fallback value if conversion fails
 * @returns number
 */
export function masterDataToNumberWithFallback(
	masterData: { id: string | number } | null | undefined,
	fallback: number = 0,
): number {
	const result = masterDataToNumber(masterData);
	return result ?? fallback;
}

/**
 * Transforms multiple masterdata fields at once
 * @param data - Object containing masterdata fields
 * @param fields - Array of field names to transform
 * @returns Object with transformed fields
 */
export function transformMasterDataFields<T extends Record<string, unknown>>(
	data: T,
	fields: (keyof T)[],
): Record<string, number | undefined> {
	const result: Record<string, number | undefined> = {};

	fields.forEach((field) => {
		const value = data[field];
		result[field as string] = masterDataToNumber(
			value as { id: string | number } | number | null | undefined,
		);
	});

	return result;
}

/**
 * Type guard to check if value is masterdata object
 */
export function isMasterData(
	value: unknown,
): value is { id: string | number; value?: string } {
	return value != null && typeof value === 'object' && 'id' in value;
}
